import type { FormField, DropdownData, FilteredValue, ReactiveFormValue } from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormValueUtils } from '@fitogest/reactive-form-builder/model';
import { getDropdownSelected } from '@fitogest/reactive-form-builder/utils';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';

const VARIOS_VARIETY_NAME = 'Varios';

export type ParcelasVariedadVariosCreateRequest = Pick<VisitasVariedadMesModel, 'cultivo' | 'variedad'>;

export type ParcelasVariedadVariosResult = {
    readonly created: boolean;
    readonly shouldUpdateForm: boolean;
    readonly formModelPatch?: Partial<ReactiveFormValue<ParcelasModel>>;
    readonly legacyModelPatch?: Partial<ParcelasModel>;
};

export type ParcelasVariedadVariosContext = {
    readonly env: ParcelasEnvironmentDefinition;
    readonly formFieldsDatosParcela: readonly FormField<object>[];
    readonly variedadesDropdown: DropdownData<VisitasVariedadMesModel>;
    readonly createVariedad: (
        request: ParcelasVariedadVariosCreateRequest
    ) => Promise<readonly Partial<VisitasVariedadMesModel>[] | null | undefined>;
    readonly resolveCurrentFieldValue: (fieldName: string) => unknown;
    readonly resolveCurrentDropdownRawValue: <T>(fieldName: string, value: unknown, selected: unknown) => T | null;
};

/**
 * Ensures legacy environments that accept "Varios" as an automatic variety have a valid id before submit.
 *
 * The component owns form mutation, while this adapter owns the lookup, cultivation resolution and creation rules.
 */
export class ParcelasVariedadVariosAdapter {

    public static async ensureVariosSelection(
        context: ParcelasVariedadVariosContext
    ): Promise<ParcelasVariedadVariosResult> {
        if (!context.env.variedadCanBeVarios) {
            return ParcelasVariedadVariosAdapter.createUnchangedResult();
        }

        const existingVarios = ParcelasVariedadVariosAdapter.findExistingVariosOption(context.variedadesDropdown);

        if (existingVarios) {
            return ParcelasVariedadVariosAdapter.createExistingVariosResult(existingVarios);
        }

        const cultivoRaw = ParcelasVariedadVariosAdapter.resolveCurrentCultivoRaw(context);

        if (!cultivoRaw) {
            return ParcelasVariedadVariosAdapter.createUnchangedResult();
        }

        const createdRows = await context.createVariedad({
            cultivo: cultivoRaw.cultivo ?? '',
            variedad: VARIOS_VARIETY_NAME,
        });
        const createdId = createdRows?.[0]?.id ?? '';

        return {
            created: true,
            shouldUpdateForm: createdRows?.[0] !== undefined,
            formModelPatch: {
                id_variedad: createdId,
            },
        };
    }

    private static createUnchangedResult(): ParcelasVariedadVariosResult {
        return {
            created: false,
            shouldUpdateForm: false,
        };
    }

    private static createExistingVariosResult(
        option: FilteredValue<VisitasVariedadMesModel>
    ): ParcelasVariedadVariosResult {
        const rawValue = ReactiveFormValueUtils.resolveDropdownOptionRawValue<VisitasVariedadMesModel>(option);
        const id = String(
            ReactiveFormValueUtils.extractComparableId(option.value, 'id') ?? rawValue?.id ?? '',
        );

        return {
            created: false,
            shouldUpdateForm: false,
            legacyModelPatch: {
                id_variedad: id,
            },
        };
    }

    private static findExistingVariosOption(
        variedadesDropdown: DropdownData<VisitasVariedadMesModel>
    ): FilteredValue<VisitasVariedadMesModel> | null {
        return variedadesDropdown.filtered.find((option) => {
            const rawValue = ReactiveFormValueUtils.resolveDropdownOptionRawValue<VisitasVariedadMesModel>(option);

            return rawValue?.variedad === VARIOS_VARIETY_NAME;
        }) ?? null;
    }

    private static resolveCurrentCultivoRaw(context: ParcelasVariedadVariosContext): VisitasVariedadMesModel | null {
        const fieldName = context.env.hasPlantas || !context.env.activateApiSIEX
            ? 'id_cultivo_legacy'
            : 'id_cultivo';
        const field = context.formFieldsDatosParcela.find((currentField) => currentField.name === fieldName);

        if (!field) {
            return null;
        }

        const value = context.resolveCurrentFieldValue(field.name);
        const selected = getDropdownSelected(field.input.data as DropdownData<VisitasVariedadMesModel>);

        return context.resolveCurrentDropdownRawValue<VisitasVariedadMesModel>(field.name, value, selected);
    }
}
